import { useCallback, useMemo } from "react";
import { useLocation } from "@rotorstudios/fe-modules";
import {
  iDataLayerEvent,
  iTFYAdjustLeaseOptionsEvent,
  iTFYCompareVehicleGradesEvent,
  iTFYCompareVehicleGradesSelectEvent,
  iTFYCopyVehicleConfigurationLinkEvent,
  iTFYErrorMessageEvent,
  iTFYLaunchAREvent,
  iTFYPageViewEvent,
  iTFYSaveVehicleConfigurationEvent,
  iTFYSelectGradeEvent,
  iTFYSelectTabEvent,
  iTFYSendVehicleConfigurationEvent,
  iTFYViewCorePageEvent,
  iTFYViewScreenEvent,
} from "@rotorstudios/fe-incubeta-ga-types";
import striptags from "striptags";

import {
  CME_OPTIONSET_ACCESSORIES,
  CME_OPTIONSET_COLOUR,
  CME_OPTIONSET_GRADE,
  CME_OPTIONSET_MVOB,
  CME_OPTIONSET_TRANSMISSION,
  CME_OPTIONSET_TRIM,
} from "../../Data/static";
import { useCmeContext } from "../../fe-cme/CMEProvider";
import { GA_AT, GaEvent } from "./static";
import type {
  iTFYInteract3DEvent,
  iTFYLaunchARAvailabilityEvent,
  iTFYSelectAccessoryEvent,
  iTFYSelectColourTrimEvent,
} from "./types";
import { useDataContext } from "../../Data/DataProvider";
import { convertKeysToSnakeCase } from "../../../helpers/utils";

type GaEventOptions = {
  includeDefaultParams?: boolean;
  includeVehicleParams?: boolean;
  includeVehicleConfigParams?: boolean;
  includeAccessories?: boolean;
  extraParams?: Record<string, string>;
};

const useGAEvents = () => {
  const { cmeSiloUiData, dealershipData, userData } = useDataContext();
  const { cmeOptions } = useCmeContext();
  const {
    search: { urlSearchParams },
  } = useLocation();

  const getCMESelectedOptions = useCallback(
    () => cmeOptions?.selectedOptions,
    [cmeOptions],
  );

  const getAccessories = useCallback(() => {
    const selectedAccessories = getCMESelectedOptions()?.get(CME_OPTIONSET_ACCESSORIES);

    const accessories = (selectedAccessories || []).map((accessory: any) => {
      const { clientName, uiGroup } =
        cmeSiloUiData?.features?.accessories?.options?.[accessory] || {};
      return {
        accessoryName: clientName,
        accessoryCategory: uiGroup,
      };
    });

    return accessories || [];
  }, [cmeSiloUiData, getCMESelectedOptions]);

  const getVehicleParams = useCallback(() => {
    const selectedGrade = getCMESelectedOptions()?.get(CME_OPTIONSET_GRADE);
    return {
      vehicleGrade: cmeSiloUiData?.features?.grade?.options?.[selectedGrade]
        ?.clientName as string,
      vehicleModel: cmeSiloUiData?.model?.title as string,
    };
  }, [getCMESelectedOptions, cmeSiloUiData]);

  const getVehicleDetails = useCallback(() => {
    const getOptionDetails = (optionSet: any) => {
      const selectedOption = getCMESelectedOptions()?.get(optionSet);
      return cmeSiloUiData?.features?.[optionSet]?.options?.[selectedOption]?.clientName;
    };

    return {
      vehicleGradeVariation: striptags(getCMESelectedOptions()?.get(CME_OPTIONSET_GRADE)),
      vehicleColour: striptags(getOptionDetails(CME_OPTIONSET_COLOUR)),
      vehicleTrim: striptags(getOptionDetails(CME_OPTIONSET_TRIM)),
    };
  }, [cmeSiloUiData, getCMESelectedOptions]);

  const getMaterialCode = useCallback((selectedOptions: Map<string, any>) => {
    const mvob = selectedOptions?.get(CME_OPTIONSET_MVOB);
    const interiorTrim = selectedOptions?.get(CME_OPTIONSET_TRIM);
    const colour = selectedOptions?.get(CME_OPTIONSET_COLOUR);

    if (mvob && interiorTrim && colour) {
      return `${mvob}${interiorTrim}${colour}`;
    }

    return undefined;
  }, []);

  const getVehicleConfigParams = useCallback(() => {
    const { vehicleGradeVariation, vehicleColour, vehicleTrim } = getVehicleDetails();
    return {
      vehicleColour,
      vehicleGradeVariation,
      vehicleTrim,
      vehicleTransmission:
        getCMESelectedOptions()?.get(CME_OPTIONSET_TRANSMISSION) || GA_AT,
      vehicleMaterialCode:
        getMaterialCode(getCMESelectedOptions()) ?? userData?.materialCode,
      accessories: getAccessories(),
    };
  }, [
    getVehicleDetails,
    getCMESelectedOptions,
    getMaterialCode,
    userData?.materialCode,
    getAccessories,
  ]);

  const getDefaultParams = useCallback(
    () => ({
      pmaName: dealershipData?.pmaName as string,
      sapCode: (dealershipData?.sapCode as string) ?? (urlSearchParams?.SC as string),
      vehicleFranchise: "Toyota" as string,
    }),
    [dealershipData, urlSearchParams],
  );

  const getEventParams = useCallback(
    (event: GaEvent, options: GaEventOptions = {}) => {
      const { accessories, vehicleMaterialCode, ...vehicleConfigParams } =
        getVehicleConfigParams();

      const { includeDefaultParams = true, includeVehicleParams = true } = options;

      const defaultParams = includeDefaultParams ? getDefaultParams() : {};
      const vehicleParams = includeVehicleParams ? getVehicleParams() : {};
      const vehicleAccessories = options?.includeAccessories ? accessories : {};
      const vehicleConfigs = options?.includeVehicleConfigParams
        ? vehicleConfigParams
        : {};

      const payload = {
        event,
        ...defaultParams,
        ...vehicleParams,
        ...vehicleConfigs,
        ...vehicleAccessories,
        ...options?.extraParams,
      };

      const formattedEventParams = convertKeysToSnakeCase(payload);
      return formattedEventParams;
    },
    [getDefaultParams, getVehicleConfigParams, getVehicleParams],
  );

  const removeUndefinedKeys = (obj: any) =>
    Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined));

  const sendGAEvent = useCallback(
    <T extends iDataLayerEvent>(eventName: GaEvent, options: GaEventOptions = {}) => {
      const event = getEventParams(eventName, options);
      globalThis.window?.dataLayer?.push(removeUndefinedKeys(event) as T);
    },
    [getEventParams],
  );

  const eventFactory = useMemo<Partial<Record<GaEvent, (extraParams?: any) => any>>>(
    () => ({
      [GaEvent.ACCORDION_CLICK]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.ACCORDION_CLICK, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.ADJUST_LEASE_OPTION]: (extraParams?: any) =>
        sendGAEvent<iTFYAdjustLeaseOptionsEvent>(GaEvent.ADJUST_LEASE_OPTION, {
          extraParams,
        }),

      [GaEvent.BUTTON_CLICKS]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.BUTTON_CLICKS, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.BOOK_TEST_DRIVE]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.BOOK_TEST_DRIVE, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.EMAIL_DEALER]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.EMAIL_DEALER, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.CLICK_TO_CALL]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.CLICK_TO_CALL, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.CONFIGURATOR_INTERACT_3D]: (extraParams?: any) =>
        sendGAEvent<iTFYInteract3DEvent>(GaEvent.CONFIGURATOR_INTERACT_3D, {
          includeAccessories: false,
          extraParams,
        }),

      [GaEvent.CONFIGURATOR_SELECT_ACCESSORY]: (extraParams?: any) =>
        sendGAEvent<iTFYSelectAccessoryEvent>(GaEvent.CONFIGURATOR_SELECT_ACCESSORY, {
          includeAccessories: false,
          extraParams,
        }),

      [GaEvent.CONFIGURATOR_SELECT_COLOUR_TRIM]: (extraParams?: any) =>
        sendGAEvent<iTFYSelectColourTrimEvent>(GaEvent.CONFIGURATOR_SELECT_COLOUR_TRIM, {
          includeAccessories: false,
          extraParams,
        }),

      [GaEvent.CONFIGURATOR_SELECT_GRADE]: (extraParams?: any) =>
        sendGAEvent<iTFYSelectGradeEvent>(GaEvent.CONFIGURATOR_SELECT_GRADE, {
          extraParams,
        }),

      [GaEvent.CONFIGURATOR_SELECT_TAB]: (extraParams?: any) =>
        sendGAEvent<iTFYSelectTabEvent>(GaEvent.CONFIGURATOR_SELECT_TAB, {
          extraParams,
        }),

      [GaEvent.DEALER_NEXT_STEPS]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.DEALER_NEXT_STEPS, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.DOWNLOAD_FULL_SPEC_SHEET]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.DOWNLOAD_FULL_SPEC_SHEET, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.COPY_VEHICLE_CONFIGURATION_LINK]: () =>
        sendGAEvent<iTFYCopyVehicleConfigurationLinkEvent>(
          GaEvent.COPY_VEHICLE_CONFIGURATION_LINK,
          {
            includeVehicleConfigParams: true,
          },
        ),

      [GaEvent.DOWNLOAD_BROCHURE_PDF]: () =>
        sendGAEvent<iDataLayerEvent>(GaEvent.DOWNLOAD_BROCHURE_PDF, {
          includeVehicleConfigParams: true,
        }),

      [GaEvent.SUBMIT_SAVE_VEHICLE_FORM]: () =>
        sendGAEvent<iTFYSendVehicleConfigurationEvent>(GaEvent.SUBMIT_SAVE_VEHICLE_FORM, {
          includeVehicleConfigParams: true,
        }),

      [GaEvent.ERROR_MESSAGE]: (extraParams?: any) =>
        sendGAEvent<iTFYErrorMessageEvent>(GaEvent.ERROR_MESSAGE, {
          extraParams,
        }),

      [GaEvent.LAUNCH_AR]: () => sendGAEvent<iTFYLaunchAREvent>(GaEvent.LAUNCH_AR),

      [GaEvent.LAUNCH_AR_AVAILABILITY]: (extraParams?: any) =>
        sendGAEvent<iTFYLaunchARAvailabilityEvent>(GaEvent.LAUNCH_AR_AVAILABILITY, {
          extraParams,
        }),

      [GaEvent.TOYOTA_FOR_YOU_CORE_PAGE]: (extraParams?: any) =>
        sendGAEvent<iTFYViewCorePageEvent>(GaEvent.TOYOTA_FOR_YOU_CORE_PAGE, {
          extraParams,
        }),

      [GaEvent.PAGE_VIEW]: (extraParams?: any) =>
        sendGAEvent<iTFYPageViewEvent>(GaEvent.PAGE_VIEW, {
          extraParams,
        }),

      [GaEvent.SAVE_VEHICLE_CONFIGURATION]: (extraParams?: any) =>
        sendGAEvent<iTFYSaveVehicleConfigurationEvent>(
          GaEvent.SAVE_VEHICLE_CONFIGURATION,
          {
            includeVehicleConfigParams: true,
            extraParams,
          },
        ),

      [GaEvent.SPEC_COMPARE_GRADES]: (extraParams?: any) =>
        sendGAEvent<iTFYCompareVehicleGradesEvent>(GaEvent.SPEC_COMPARE_GRADES, {
          includeVehicleParams: false,
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.SPEC_SELECT_GRADE]: (extraParams?: any) => {
        sendGAEvent<iTFYCompareVehicleGradesSelectEvent>(GaEvent.SPEC_SELECT_GRADE, {
          includeVehicleParams: false,
          includeVehicleConfigParams: true,
          extraParams,
        });
      },

      [GaEvent.VIEW_FEATURES]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.VIEW_FEATURES, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.VIEW_FINANCE_OPTIONS]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.VIEW_FINANCE_OPTIONS, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.VIEW_IN_AR]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.VIEW_IN_AR, {
          includeVehicleConfigParams: true,
          extraParams,
        }),

      [GaEvent.VIEW_SCREEN]: (extraParams?: any) =>
        sendGAEvent<iTFYViewScreenEvent>(GaEvent.VIEW_SCREEN, {
          extraParams,
        }),

      [GaEvent.VIEW_SPECS_AND_GRADES]: (extraParams?: any) =>
        sendGAEvent<iDataLayerEvent>(GaEvent.VIEW_SPECS_AND_GRADES, {
          includeVehicleConfigParams: true,
          extraParams,
        }),
    }),
    [sendGAEvent],
  );

  return {
    getEventParams,
    sendGAEvent,
    eventFactory,
    getVehicleParams,
    getVehicleConfigParams,
  };
};

export default useGAEvents;
